import { ref } from "vue";

import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import { LinkedinSocialIntegration } from "shared/resources";

import {
  useOpenAuthWindow,
  usePollWindowClose,
  useValidEvent,
} from "./composables";
import { type SocialUserRecord } from "./types";

const LINKEDIN_STORAGE_KEY = "linkedinOAuth";

type LinkedInCompanyData = {
  id: string;
  label: string;
  profilePhotoUrl: string;
};

// eslint-disable-next-line import/prefer-default-export
export function useLinkedInAuth() {
  const isLoggedIn = ref(false);
  const isScriptLoaded = ref(true); // LinkedIn doesn't require SDK loading
  const openedWindow = ref<Window | null>(null);
  const isValidEvent = useValidEvent(LINKEDIN_STORAGE_KEY);
  const openAuthWindow = useOpenAuthWindow("LinkedIn");

  const {
    abortController,
    cleanup,
    isProcessing,
    pollForWindowClose,
    userExitedLoginProcess,
    windowClosedByAuth,
  } = usePollWindowClose({
    openedWindow,
    storageKey: LINKEDIN_STORAGE_KEY,
  });

  const getCompanies = async (
    accessToken: string
  ): Promise<SocialUserRecord[]> => {
    const { data: companies } = await $streemApiV1.get<LinkedInCompanyData[]>(
      "social_integrations/linkedin/companies",
      {
        transformKeys: true,
        params: { accessToken },
        signal: abortController.value.signal,
      }
    );

    return companies.map((company) => ({
      accessToken,
      id: company.id,
      metadata: { profilePhotoUrl: company.profilePhotoUrl },
      name: company.label,
      selected: true,
      type: "linkedin",
    }));
  };

  const continueAuth = async (event: StorageEvent) => {
    try {
      const oAuth = JSON.parse(event.newValue ?? "{}");

      // Mark that the window is being closed by the auth flow, not by user
      windowClosedByAuth.value = true;
      openedWindow.value?.close();

      const { accessToken } = oAuth;
      const companies = await getCompanies(accessToken);

      isLoggedIn.value = true;
      userExitedLoginProcess.value = false;

      return companies;
    } catch {
      throw new Error(
        getLocaleText(
          "social_engage_add_linkedin_account.fetching_linkedin_companies_error"
        )
      );
    }
  };

  const login = (): Promise<SocialUserRecord[]> => {
    cleanup();
    isProcessing.value = true;
    userExitedLoginProcess.value = false;
    isLoggedIn.value = false;

    try {
      openedWindow.value = openAuthWindow();
    } catch {
      throw new Error(
        getLocaleText(
          "social_engage_add_linkedin_account.start_linkedin_authorisation_error"
        )
      );
    }

    const windowClosePromise = pollForWindowClose();

    const authPromise: Promise<SocialUserRecord[]> = new Promise(
      (resolve, reject) => {
        const handleStorage = async (event: StorageEvent) => {
          if (!isValidEvent(event)) return;

          try {
            const result = await continueAuth(event);
            resolve(result);
          } catch (error) {
            isLoggedIn.value = false;
            abortController.value.abort();

            reject(error);
          } finally {
            window.removeEventListener("storage", handleStorage);
            cleanup();
          }
        };

        window.addEventListener("storage", handleStorage);
      }
    );

    return Promise.race([windowClosePromise, authPromise]);
  };

  return {
    Integration: LinkedinSocialIntegration,
    abortController,
    isLoggedIn,
    isProcessing,
    isScriptLoaded,
    userExitedLoginProcess,

    login,
  };
}
